import React, { useState, useRef } from "react";
import ImageLightbox, { useLightbox } from './ImageLightbox'; 


import img1 from '../../Assets/developmentKit/1.png'
import img2 from '../../Assets/developmentKit/2.png'
import img3 from '../../Assets/developmentKit/3.png'

/**
 * ProductInfoTabs
 * Drop-in tab component for a product page: Details / Specification / Images.
 * Styled using VerdeMobility's actual design tokens (captured from
 * EVChargingSolutions.jsx): `containers-min` container, `font-gilroy`
 * headings, `font-poppins` body copy, `text-verde` brand accent,
 * `bg-gradient-opa` overlay panel, `rounded-2xl` cards.
 *
 * Usage:
 * <ProductInfoTabs
 *   details="<p>Rich HTML or plain text about the product...</p>"
 *   specifications={[
 *     { label: "Power Output", value: "60 kW" },
 *     { label: "Connector Type", value: "CCS2" },
 *   ]}
 *   images={[
 *     { src: "/images/products/levdc-front.jpg", alt: "LEV DC Fast Charger - front view" },
 *   ]}
 * />
 *
 * Notes:
 * - `details` can be a plain string, JSX, or an HTML string (rendered via
 *   dangerouslySetInnerHTML only when it's a string — pass JSX directly otherwise).
 * - If this sits inside a page that already wraps content in `containers-min`,
 *   drop the `wrapInContainer` prop to false so you don't double up the container.
 * - Fully keyboard accessible: arrow keys move focus between tabs, tab panel is
 *   announced via ARIA.
 * - The "What's in the box / Who is it for" lists now live in their own
 *   component, `ProductBoxAndAudience.jsx` — render that separately next to
 *   this component wherever you need it.
 */

const TABS = [
  { id: "details", label: "Details" },
  { id: "specification", label: "Controller Specifications" },
  { id: "images", label: "Images" },
];

const CheckCircleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="rgb(35, 180, 85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

const KEY_BENEFITS = [
  {
    title: "Faster Go-to-Market",
    description: "Reduce development time and accelerate the launch of your AC charger.",
  },
  {
    title: "Reduced Development Cost",
    description: "Minimize engineering effort, prototyping expenses and repeated hardware development.",
  },
  {
    title: "Reduced Inventory Management",
    description: "Eliminate the need to source, stock and manage multiple individual electronic components.",
  },
  {
    title: "Reliable & Time-Tested Industrial-Grade Controller",
    description: "Built on a proven controller platform designed for reliable EV charging applications.",
  },
  {
    title: "Minimal Technology Expertise Required",
    description: "Build a functional AC charger with limited EV charging and embedded systems knowledge.",
  },
  {
    title: "Easy Customization",
    description: "Adapt the charger solution to your enclosure, branding, communication requirements and application needs.",
  },
  {
    title: "Integrate with Any CMS of Your Choice",
    description: "Build OCPI-ready chargers that can integrate seamlessly with your preferred Charge Management System (CMS), giving you the flexibility to choose or switch your backend platform without being locked into a specific CMS provider.",
  },
  {
    title: "OCPP 1.6J Compliant",
    description: "Build chargers with OCPP 1.6J compliance without the complexity, time and cost of developing and maintaining the communication firmware from scratch.",
  },
];

const DEFAULT_DETAILS = (
  <div className="space-y-3">
   <p className="pb-4 text-xl">
     The VerdeMobility AC Charger Development Kit is a comprehensive development and integration platform designed to help EV charger manufacturers, EVSE developers, system integrators, and R&D teams accelerate the development and validation of AC charging solutions. The platform brings together essential charging, control, communication, metering, safety, and user-interface hardware in a ready-to-integrate environment, providing a practical foundation for prototyping, system evaluation, and product development.
    </p>
    <p className="pb-4 text-xl">The hardware platform combines a 4.3" Touch Screen Graphic Display, RFID-based user authentication, RGB LED status indication, emergency-stop functionality, energy metering, communication interfaces, and charging output connectivity. Supporting up to 32 A / 7.5 kW AC charging, with GSM/4G and Wi-Fi connectivity and configurable Type 2 or 3-Pin / IEC 60309 output options, the kit enables developers to build and evaluate AC charging configurations aligned with their application requirements. </p>

    <p className="pb-4 text-xl">The kit can also be complemented with VerdeMobility’s Charging Management Solution (CMS) as an optional add-on, providing a connected software layer for charger monitoring, control, user management, and charging-session management. This allows development teams to evaluate both the physical charging hardware and the connected software experience, while retaining the flexibility to use the CMS based on their development and application requirements.</p>
   
    <p className="pb-4 text-xl">Designed for rapid prototyping, engineering validation, application development, and product integration, the VerdeMobility AC Charger Development Kit provides a scalable starting point for organizations looking to develop, customize, test, and take their own AC EV charging solutions toward deployment.</p>

    <div className="pt-4">
      <p className="font-bold text-3xl pb-6">Key Benefits of AC Charger Development Kit:</p>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
        {KEY_BENEFITS.map((benefit, i) => (
          <div key={benefit.title} className="flex gap-3">
            <CheckCircleIcon className="w-6 h-6 shrink-0 mt-1" />
            <div>
              <p className="font-bold text-xl font-gilroy">
                 {benefit.title}
              </p>
              <p className="text-black/70 text-lg">{benefit.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const DEFAULT_SPECIFICATIONS = [
  { label: "Input Voltage", value: "230 VAC ±15%, Single Phase" },
  { label: "AC Input Connection", value: "P + N + PE" },
  { label: "Frequency", value: "50 Hz" },
  { label: "AC Output Current", value: "Up to 32A" },
  { label: "Maximum Output Power", value: "7.5 kW" },
  { label: "Connector Type", value: "Type 2 AC (IEC 62196-2), 3-Pin" },
  { label: "No. of Connectors", value: "1" },
  { label: "Operating Temperature", value: "-30°C to 70°C" },
  { label: "Storage Temperature", value: "0°C to 70°C" },
  { label: "Humidity", value: "5% to 95%" },
  { label: "Display", value: "4.3\" Touch Screen Graphic Display" },
  { label: "Network Connection", value: "GSM Modem (4G LTE fallback to 2G) and Wi-Fi" },
  { label: "Standards Supported", value: "IEC 61851-1, IEC 61851-21-2, REACH, RoHS" },
  { label: "Dimensions (L x W x H)", value: "224.27 x 170.50 x 50 mm" },
  { label: "Energy Measurement", value: "Internal Single Phase Metering Supported" },
];


const DEFAULT_IMAGES = [
   { src: img2, alt: "LEV DC Fast Charger - side view" },
   { src: img3, alt: "LEV DC Fast Charger - connector close-up" }, 
   
  { src: img1, alt: "LEV DC Fast Charger - front view" },
 
 
];

export default function ProductInfoTabs({
  details = DEFAULT_DETAILS,
  specifications = DEFAULT_SPECIFICATIONS,
  images = DEFAULT_IMAGES,
  wrapInContainer = true,
  
}) {
  const [activeTab, setActiveTab] = useState("details");
  const tabRefs = useRef({});
  const lightbox = useLightbox(); // add this

  const handleKeyDown = (e, index) => {
    let newIndex = null;
    if (e.key === "ArrowRight") newIndex = (index + 1) % TABS.length;
    if (e.key === "ArrowLeft") newIndex = (index - 1 + TABS.length) % TABS.length;
    if (newIndex !== null) {
      e.preventDefault();
      const nextTab = TABS[newIndex];
      setActiveTab(nextTab.id);
      tabRefs.current[nextTab.id]?.focus();
    }
  };

  const content = (
    <div className="w-full">
      {/* Section heading — same pattern as EVChargingSolutions: h3 + font-gilroy + <b> emphasis */}
      <div className="mb-10">
        <h3 className="font-gilroy">
          Everything you need to know
        </h3>
      </div>

      {/* Tab headers */}
      <div
        role="tablist"
        aria-label="Product information"
        className="flex flex-wrap justify-center gap-8 border-b border-black/10 mb-8"
      >
        {TABS.map((tab, index) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              ref={(el) => (tabRefs.current[tab.id] = el)}
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`panel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`relative pb-4 font-gilroy text-xl sm:text-2xl font-bold transition-colors duration-300 outline-none
                ${isActive ? "text-verde" : "text-black/40 hover:text-black/70"}`}
            >
              {tab.label}
              {isActive && (
                <span className="absolute left-0 -bottom-px h-[3px] w-full bg-verde rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      <div>
        <div
          role="tabpanel"
          id="panel-details"
          aria-labelledby="tab-details"
          hidden={activeTab !== "details"}
          className="leading-normal font-poppins text-lg text-black/80"
        >
          {typeof details === "string" ? (
            <div dangerouslySetInnerHTML={{ __html: details }} />
          ) : (
            details || <p className="italic text-black/40">No details available.</p>
          )}
        </div>

        <div
          role="tabpanel"
          id="panel-specification"
          aria-labelledby="tab-specification"
          hidden={activeTab !== "specification"}
        >
          {specifications.length > 0 ? (
            <div className="rounded-2xl overflow-hidden border border-black/10">
              <table className="w-full font-poppins text-base sm:text-lg">
                <tbody>
                  {specifications.map((spec, i) => (
                    <tr
                      key={spec.label}
                      className={i % 2 === 0 ? "bg-black/[0.02]" : "bg-transparent"}
                    >
                      <td className="px-5 py-3 font-gilroy font-bold text-black/70 w-1/3 border-b border-black/10 border-r ">
                        {spec.label}
                      </td>
                      <td className="px-5 py-3 text-black/80 border-b border-black/10  ">
                        {spec.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="italic text-black/40 font-poppins">No specifications available.</p>
          )}
        </div>

        <div
          role="tabpanel"
          id="panel-images"
          aria-labelledby="tab-images"
          hidden={activeTab !== "images"}
        >
          {images.length > 0 ? (
            <div className="flex flex-wrap md:flex-nowrap sm:grid-cols-3 gap-6">
              {images.map((img, i) => (
                <div
                  key={i}
                  className="group relative shadow-[0_0_15px_rgba(0,0,0,0.15)] hover:shadow-[0_0_20px_rgba(0,0,0,0.25)] rounded-2xl overflow-hidden aspect-square cursor-zoom-in"
                  onClick={() => lightbox.open(img.src)}
                >
                  <img
                    loading="lazy"
                    src={img.src}
                    alt={img.alt || `Product image ${i + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    
                  />
                  {img.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-opa py-2 px-3 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="text-sm font-poppins">{img.caption}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="italic text-black/40 font-poppins">No images available.</p>
          )}
        </div>
      </div>
    </div>

  );

  return wrapInContainer ? (
    <div className="containers-min mx-auto px-4 py-12">{content}
       <ImageLightbox {...lightbox} alt="Product image" /></div>
  ) : (
    content
  );
}